'use strict';

/**
 * @ngdoc service
 * @name scrapQcrmApp.tableState
 * @description
 * Keeps the state of a list page in the URL query (filters, search, sort, page, page size, tab, dates, scroll,
 * the open record), so Back / Forward / refresh / a shared link show the same view.
 *
 * One call per page controller, after the page's default values are set and before data is loaded:
 *
 *   var table = tableState.page($scope, 'c', {
 *       controls: {                                   // values outside the grid (scope paths)
 *           q:   { model: 'searchText', type: 'string', history: 'search' },
 *           d:   { model: 'date', type: 'daterange', always: true },   // always: kept in the URL even as default
 *           s:   { model: 'statusFilter', validate: fn(v), onRestore: fn(v) },  // onRestore: reload after Back / Forward
 *           p:   { model: 'currentPage', type: 'int', history: 'replace' }
 *       },
 *       grids: { g: 'gridOptions' },                    // ag-Grid gridOptions (scope paths); filter / sort / page / page size / top row
 *       dataTables: { t: 'table[datatable]' },          // DataTables (selector); page / page size / order / search
 *       ngTables: { t: 'tableParams' },                 // ng-table params (scope path); page / page size
 *       record: { model: 'consumerId', open: fn(id), close: fn() }   // in-page record view (c.view=<id>)
 *   });
 *   table.backFromRecord();  table.openInNewTab(id);  table.isModifiedClick(event);  table.recordInUrl();
 *   tableState.backTo('/customers/community-customers');   // a record page's Back to a list on another page
 *
 * Types: string, int, bool, date, day ("DD-MMM-YYYY" string), daterange, list, json. Dates are local calendar days.
 * URL keys are "<ns>.<key>" (e.g. c.q, c.g.f), so several tables on one page never collide.
 * History: filters, search (after typing stops), tabs, dates and opening a record add a history entry;
 * page, page size, sort and scroll replace the current entry. Invalid values in the URL are ignored (defaults used)
 * and removed from it; a page past the end shows the last page.
 * Opening a page from the sidebar always shows its default view.
 */
angular.module('scrapQcrmApp')
    .factory('tableState', function ($location, $rootScope, $state, $timeout, $window, $parse, $document) {

        var SEARCH_DEBOUNCE = 600;
        var registrations = [];
        var applyingUrl = 0; // > 0 while values from the URL are being applied (no writes then)
        var lastWrittenUrl = null;
        var lastUserAction = 0; // filter changes right after a click / key press are the user's (new history entry)

        $document.on('mousedown keydown touchstart', function () {
            lastUserAction = Date.now();
        });

        function byUser() {
            return Date.now() - lastUserAction < 2000;
        }

        // ag-Grid 24.1: a set filter whose grid was destroyed (page left, grid re-created) can still refresh its
        // values a moment later and throws "reading 'refresh'"; skip that refresh, the filter is gone anyway
        var SetFilter = $window.agGrid && $window.agGrid.SetFilter;
        if (SetFilter && SetFilter.prototype.refresh && !SetFilter.prototype.__safeRefresh) {
            var setFilterRefresh = SetFilter.prototype.refresh;
            SetFilter.prototype.refresh = function () {
                if (this.virtualList) {
                    return setFilterRefresh.apply(this, arguments);
                }
            };
            SetFilter.prototype.__safeRefresh = true;
        }

        // ---------- encoding ----------

        function b64urlEncode(text) {
            return $window.btoa(unescape(encodeURIComponent(text))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        }

        function b64urlDecode(text) {
            var s = String(text).replace(/-/g, '+').replace(/_/g, '/');
            while (s.length % 4) {
                s += '=';
            }
            return decodeURIComponent(escape($window.atob(s)));
        }

        function toYmd(value) {
            if (!value) {
                return null;
            }
            var m = moment(value);
            return m.isValid() ? m.format('YYYY-MM-DD') : null;
        }

        function fromYmd(text) {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
                return undefined;
            }
            var m = moment(text, 'YYYY-MM-DD', true); // local calendar day
            return m.isValid() ? m : undefined;
        }

        // encode: value -> string or null (null = not in the URL); decode: string -> value, undefined = invalid
        var codecs = {
            string: {
                encode: function (v) {
                    return (v === null || v === undefined || v === '') ? null : String(v);
                },
                decode: function (s) {
                    return String(s).length > 500 ? undefined : String(s);
                }
            },
            int: {
                encode: function (v) {
                    var n = parseInt(v, 10);
                    return isNaN(n) ? null : String(n);
                },
                decode: function (s) {
                    return /^-?\d{1,9}$/.test(s) ? parseInt(s, 10) : undefined;
                }
            },
            bool: {
                encode: function (v) {
                    return v === true ? '1' : (v === false ? '0' : null);
                },
                decode: function (s) {
                    return s === '1' ? true : (s === '0' ? false : undefined);
                }
            },
            date: {
                encode: toYmd,
                decode: fromYmd
            },
            // one day kept by the page as a "DD-MMM-YYYY" string (single date pickers)
            day: {
                encode: function (v) {
                    if (!v) {
                        return null;
                    }
                    var m = moment(v, ['DD-MMM-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
                    if (!m.isValid() && v instanceof Date) {
                        m = moment(v);
                    }
                    return m.isValid() ? m.format('YYYY-MM-DD') : null;
                },
                decode: function (s) {
                    var m = fromYmd(s);
                    return m ? m.format('DD-MMM-YYYY') : undefined;
                }
            },
            // {startDate, endDate} as used by the date range pickers
            daterange: {
                encode: function (v) {
                    if (!v || !v.startDate || !v.endDate) {
                        return null;
                    }
                    var a = toYmd(v.startDate);
                    var b = toYmd(v.endDate);
                    return a && b ? a + '~' + b : null;
                },
                decode: function (s) {
                    var parts = String(s).split('~');
                    if (parts.length !== 2) {
                        return undefined;
                    }
                    var a = fromYmd(parts[0]);
                    var b = fromYmd(parts[1]);
                    if (!a || !b || b.isBefore(a)) {
                        return undefined;
                    }
                    return { startDate: a, endDate: b.clone().endOf('day') };
                }
            },
            list: {
                encode: function (v) {
                    if (!angular.isArray(v) || !v.length) {
                        return null;
                    }
                    return v.map(function (x) {
                        return encodeURIComponent(String(x));
                    }).join(',');
                },
                decode: function (s) {
                    return String(s).split(',').map(function (x) {
                        return decodeURIComponent(x);
                    });
                }
            },
            json: {
                encode: function (v) {
                    if (v === null || v === undefined || (angular.isObject(v) && !Object.keys(v).length)) {
                        return null;
                    }
                    return b64urlEncode(angular.toJson(v));
                },
                decode: function (s) {
                    try {
                        return angular.fromJson(b64urlDecode(s));
                    } catch (e) {
                        return undefined;
                    }
                }
            }
        };

        // ---------- URL writes ----------

        function currentSearch() {
            return angular.copy($location.search());
        }

        // history: 'push' (new entry) or 'replace' (current entry)
        function writeParams(changes, history) {
            var search = currentSearch();
            var changed = false;
            angular.forEach(changes, function (value, key) {
                if (value === null || value === undefined) {
                    if (search.hasOwnProperty(key)) {
                        delete search[key];
                        changed = true;
                    }
                } else if (search[key] !== value) {
                    search[key] = value;
                    changed = true;
                }
            });
            if (!changed) {
                return;
            }
            $location.search(search);
            if (history === 'replace') {
                $location.replace();
            }
            lastWrittenUrl = $location.url();
        }

        // ---------- registration ----------

        function page(scope, ns, config) {
            config = config || {};
            var reg = {
                scope: scope,
                ns: ns,
                stateName: $state.current.name,
                path: $location.path(),
                controls: [],
                grids: [],
                dataTables: [],
                ngTables: [],
                record: null,
                cameFromList: false
            };
            registrations.push(reg);

            angular.forEach(config.controls || {}, function (def, key) {
                var getter = $parse(def.model);
                var codec = codecs[def.type || 'string'];
                var control = {
                    key: ns + '.' + key,
                    getter: getter,
                    setter: getter.assign,
                    codec: codec,
                    history: def.history || 'push',
                    // always in the URL (e.g. dates that came from hidden route parameters), never dropped as "default"
                    always: !!def.always,
                    validate: def.validate,
                    onRestore: def.onRestore,
                    defaultValue: angular.copy(getter(scope)),
                    lastWrite: 0,
                    timer: null
                };
                control.defaultEncoded = codec.encode(control.defaultValue);
                reg.controls.push(control);
            });

            angular.forEach(config.grids || {}, function (path, key) {
                var gridOptions = angular.isString(path) ? $parse(path)(scope) : path;
                if (gridOptions) {
                    attachGrid(reg, ns + '.' + key, gridOptions);
                }
                if (angular.isString(path)) {
                    // gridOptions made later (or made again) by the page
                    scope.$watch(path, function (options) {
                        if (options && angular.isObject(options) && !options.__tableStateWrapped) {
                            attachGrid(reg, ns + '.' + key, options);
                        }
                    });
                }
            });

            angular.forEach(config.dataTables || {}, function (selector, key) {
                attachDataTable(reg, ns + '.' + key, selector);
            });

            angular.forEach(config.ngTables || {}, function (path, key) {
                attachNgTable(reg, ns + '.' + key, path);
            });

            if (config.record) {
                reg.record = {
                    key: ns + '.view',
                    getter: $parse(config.record.model),
                    open: config.record.open,
                    close: config.record.close
                };
            }

            // values from the URL now, before the page loads its data
            applyControls(reg, true);
            var initial = {};
            reg.controls.forEach(function (control) {
                if (control.always && $location.search()[control.key] === undefined) {
                    initial[control.key] = control.codec.encode(control.getter(scope));
                }
            });
            if (Object.keys(initial).length) {
                writeParams(initial, 'replace');
            }

            // then keep the URL in step with the page
            reg.controls.forEach(function (control) {
                scope.$watch(function () {
                    return control.codec.encode(control.getter(scope));
                }, function (encoded, previous) {
                    if (encoded === previous || applyingUrl) {
                        return;
                    }
                    writeControl(control, encoded);
                });
            });

            if (reg.record) {
                var openedFromUrl = $location.search()[reg.record.key];
                if (openedFromUrl && /^[a-zA-Z0-9_-]{1,64}$/.test(openedFromUrl)) {
                    // landed directly on a record (link / new tab / refresh)
                    $timeout(function () {
                        applyingUrl++;
                        try {
                            reg.record.open(openedFromUrl);
                        } finally {
                            applyingUrl--;
                        }
                    });
                }
                scope.$watch(function () {
                    var id = reg.record.getter(scope);
                    return id ? String(id) : null;
                }, function (id, previous) {
                    if (id === previous || applyingUrl) {
                        return;
                    }
                    var inUrl = $location.search()[reg.record.key] || null;
                    if (id === inUrl) {
                        return;
                    }
                    if (id) {
                        // opened by the user: new history entry; opened by the page itself (e.g. the only row): same entry
                        var user = byUser();
                        reg.cameFromList = user;
                        writeParams(makeChange(reg.record.key, id), user ? 'push' : 'replace');
                    } else {
                        writeParams(makeChange(reg.record.key, null), 'replace');
                    }
                });
            }

            scope.$on('$destroy', function () {
                reg.dataTables.forEach(function (dt) {
                    dt.detach();
                });
                reg.controls.forEach(function (control) {
                    if (control.timer) {
                        $timeout.cancel(control.timer);
                    }
                });
                var i = registrations.indexOf(reg);
                if (i >= 0) {
                    registrations.splice(i, 1);
                }
            });

            return {
                // in-app Back on a record: like browser Back when the user came from the list, else the list without the record
                backFromRecord: function () {
                    backFromRecord(reg);
                },
                // Ctrl / Cmd / middle click on a row: open the record in a new tab, this tab stays as it is
                openInNewTab: function (id) {
                    openInNewTab(reg, id);
                },
                isModifiedClick: isModifiedClick,
                // id of the record the URL opens (the page can skip loading its list then)
                recordInUrl: function () {
                    var id = reg.record ? $location.search()[reg.record.key] : null;
                    return id && /^[a-zA-Z0-9_-]{1,64}$/.test(id) ? id : null;
                }
            };
        }

        function makeChange(key, value) {
            var change = {};
            change[key] = value;
            return change;
        }

        function writeControl(control, encoded) {
            var value = encoded === control.defaultEncoded && !control.always ? null : encoded;
            if (control.history === 'search') {
                // typing: one history entry per pause, not per key
                if (control.timer) {
                    $timeout.cancel(control.timer);
                }
                control.timer = $timeout(function () {
                    control.timer = null;
                    var recent = Date.now() - control.lastWrite < 2500;
                    control.lastWrite = Date.now();
                    writeParams(makeChange(control.key, value), recent ? 'replace' : 'push');
                }, SEARCH_DEBOUNCE);
                return;
            }
            writeParams(makeChange(control.key, value), control.history);
        }

        // sets page values from the URL (or back to their defaults when not in the URL)
        function applyControls(reg, initial) {
            var search = $location.search();
            var cleanUp = {};
            applyingUrl++;
            try {
                reg.controls.forEach(function (control) {
                    var raw = search[control.key];
                    var value = control.defaultValue;
                    if (raw !== undefined && raw !== null && raw !== true) {
                        var decoded = control.codec.decode(raw);
                        if (decoded !== undefined && (!control.validate || control.validate(decoded))) {
                            value = decoded;
                        } else {
                            // invalid value: drop it from the URL (values that are always there get the default)
                            cleanUp[control.key] = control.always ? control.codec.encode(value) : null;
                        }
                    } else if (raw === true) {
                        cleanUp[control.key] = control.always ? control.codec.encode(value) : null;
                    }
                    var current = control.getter(reg.scope);
                    if (control.codec.encode(current) !== control.codec.encode(value)) {
                        control.setter(reg.scope, angular.copy(value));
                        if (control.onRestore && !initial) {
                            control.onRestore(value);
                        }
                    }
                });
            } finally {
                applyingUrl--;
            }
            if (Object.keys(cleanUp).length) {
                writeParams(cleanUp, 'replace');
            }
        }

        // ---------- ag-Grid ----------

        function attachGrid(reg, key, gridOptions) {
            var grid = { key: key, gridOptions: gridOptions, api: null, columnApi: null, pending: null, defaultPageSize: gridOptions.paginationPageSize,
                autoPageSize: !!gridOptions.paginationAutoPageSize }; // auto page size: the grid picks it, not kept in the URL
            reg.grids.push(grid);
            if (gridOptions.__tableStateWrapped) {
                gridOptions.__tableStateGrids.push(grid);
                return;
            }
            gridOptions.__tableStateWrapped = true;
            gridOptions.__tableStateGrids = [grid];
            var originalReady = gridOptions.onGridReady;
            if (gridOptions.api && !gridOptions.api.destroyCalled) {
                // the grid is already there
                grid.api = gridOptions.api;
                grid.columnApi = gridOptions.columnApi;
                listen(reg, grid);
                restoreGrid(reg, grid);
            }

            gridOptions.onGridReady = function (params) {
                var current = gridOptions.__tableStateGrids[gridOptions.__tableStateGrids.length - 1];
                current.api = params.api;
                current.columnApi = params.columnApi;
                listen(reg, current);
                // filters / sort / page size before the page's own ready code (which may start the server-side load)
                restoreGrid(reg, current);
                if (originalReady) {
                    return originalReady.apply(this, arguments);
                }
            };
        }

        function gridParam(grid, part) {
            return grid.key + '.' + part;
        }

        function readGridState(grid) {
            var search = $location.search();
            var state = { filter: null, sort: null, page: null, pageSize: null, row: null };
            var f = search[gridParam(grid, 'f')];
            if (f) {
                var filter = codecs.json.decode(f);
                state.filter = angular.isObject(filter) && !angular.isArray(filter) ? filter : undefined;
            }
            var s = search[gridParam(grid, 's')];
            if (s) {
                state.sort = String(s).split(',').map(function (item) {
                    var i = item.lastIndexOf(':');
                    var dir = item.slice(i + 1);
                    return i > 0 && (dir === 'asc' || dir === 'desc') ? { colId: decodeURIComponent(item.slice(0, i)), sort: dir } : null;
                }).filter(Boolean);
            }
            state.page = codecs.int.decode(search[gridParam(grid, 'p')] || '');
            state.pageSize = codecs.int.decode(search[gridParam(grid, 'ps')] || '');
            state.row = codecs.int.decode(search[gridParam(grid, 'r')] || '');
            return state;
        }

        // grids inside ng-if are destroyed (e.g. while a record is open) and created again later
        function alive(grid) {
            return !!grid.api && !grid.api.destroyCalled;
        }

        function restoreGrid(reg, grid) {
            var api = grid.api;
            var columnApi = grid.columnApi;
            if (!alive(grid)) {
                return;
            }
            if (reg.record && $location.search()[reg.record.key]) {
                return; // a record is open: the list is restored when it closes (the URL changes then)
            }
            var state = readGridState(grid);
            grid.restoring = true;
            try {
                var model = state.filter || {};
                if (state.filter === undefined) {
                    model = {};
                    writeParams(makeChange(gridParam(grid, 'f'), null), 'replace');
                }
                // only columns that exist on this grid
                var cleanModel = {};
                angular.forEach(model, function (value, colId) {
                    if (columnApi.getColumn(colId)) {
                        cleanModel[colId] = value;
                    }
                });
                grid.urlFilter = cleanModel;
                // client-side set filters can only select values once the rows are there
                if (isServerSide(grid) || hasRows(grid)) {
                    setFilter(grid, cleanModel);
                }

                var sortState = (state.sort || []).filter(function (s) {
                    return !!columnApi.getColumn(s.colId);
                }).map(function (s, i) {
                    return { colId: s.colId, sort: s.sort, sortIndex: i };
                });
                if (state.sort !== null || currentSort(grid)) {
                    columnApi.applyColumnState({ state: sortState, defaultState: { sort: null } });
                }

                var pageSize = state.pageSize && state.pageSize > 0 && state.pageSize <= 5000 ? state.pageSize : grid.defaultPageSize;
                if (grid.gridOptions.pagination && !grid.autoPageSize && pageSize && api.paginationGetPageSize && api.paginationGetPageSize() !== pageSize) {
                    api.paginationSetPageSize(pageSize);
                }
                // page and top row once the data is there
                grid.pending = { page: state.page && state.page > 1 ? state.page : 1, row: state.row,
                    filter: isServerSide(grid) || hasRows(grid) ? null : cleanModel };
                tryPending(grid);
            } finally {
                grid.restoring = false;
            }
        }

        function tryPending(grid) {
            var api = grid.api;
            if (!grid.pending || !alive(grid)) {
                return;
            }
            var total = api.paginationGetTotalPages ? api.paginationGetTotalPages() : 0;
            var rows = api.getDisplayedRowCount ? api.getDisplayedRowCount() : 0;
            if (!rows && !total) {
                return; // data not loaded yet
            }
            if (isServerSide(grid)) {
                // a server-loaded grid shows a "loading" row first: wait for the real rows and the real row count,
                // or page 2 of a refreshed page would look gone and the page would fall back to page 1
                var first = api.getDisplayedRowAtIndex ? api.getDisplayedRowAtIndex(0) : null;
                var lastFound = api.paginationIsLastPageFound ? api.paginationIsLastPageFound() : true;
                if (!first || first.stub || !first.data || !lastFound) {
                    return;
                }
            }
            var pending = grid.pending;
            grid.pending = null;
            grid.restoring = true;
            try {
                if (pending.filter) {
                    setFilter(grid, pending.filter);
                    total = api.paginationGetTotalPages ? api.paginationGetTotalPages() : total;
                }
                if (api.paginationGoToPage && total) {
                    var target = Math.min(pending.page, total); // page gone (fewer rows now): last page
                    if (api.paginationGetCurrentPage() !== target - 1) {
                        api.paginationGoToPage(target - 1);
                    }
                    if (target !== pending.page) {
                        writeParams(makeChange(gridParam(grid, 'p'), target > 1 ? String(target) : null), 'replace');
                    }
                }
                if (pending.row !== null && pending.row !== undefined && pending.row > 0) {
                    var row = Math.min(pending.row, api.getDisplayedRowCount() - 1);
                    if (row > 0) {
                        api.ensureIndexVisible(row, 'top');
                    }
                }
            } finally {
                grid.restoring = false;
            }
        }

        function isServerSide(grid) {
            return grid.gridOptions.rowModelType === 'serverSide' || grid.gridOptions.rowModelType === 'infinite';
        }

        function hasRows(grid) {
            return !!(grid.api && grid.api.getDisplayedRowCount && grid.api.getDisplayedRowCount() > 0) ||
                !!(grid.api && grid.api.getModel && grid.api.getModel().getRowCount && grid.api.getModel().getRowCount() > 0);
        }

        function setFilter(grid, model) {
            var api = grid.api;
            var was = grid.restoring;
            grid.restoring = true;
            try {
                if (!angular.equals(api.getFilterModel() || {}, model || {})) {
                    api.setFilterModel(model && Object.keys(model).length ? model : null);
                    api.onFilterChanged();
                }
            } catch (e) {
                try {
                    api.setFilterModel(null);
                } catch (ignored) {
                    // grid already gone
                }
            } finally {
                grid.restoring = was;
            }
        }

        function currentSort(grid) {
            var cols = (grid.columnApi.getColumnState() || []).filter(function (c) {
                return !!c.sort;
            }).sort(function (a, b) {
                return (a.sortIndex || 0) - (b.sortIndex || 0);
            });
            if (!cols.length) {
                return null;
            }
            return cols.map(function (c) {
                return encodeURIComponent(c.colId) + ':' + c.sort;
            }).join(',');
        }

        function listen(reg, grid) {
            var api = grid.api;
            var scrollTimer = null;
            api.addEventListener('filterChanged', function () {
                if (grid.restoring || applyingUrl) {
                    return;
                }
                var model = api.getFilterModel();
                var change = makeChange(gridParam(grid, 'f'), codecs.json.encode(model));
                change[gridParam(grid, 'p')] = null; // a new filter starts on page 1
                change[gridParam(grid, 'r')] = null;
                var user = byUser();
                if (!user && grid.urlFilter && !angular.equals(model || {}, grid.urlFilter)) {
                    return; // a data reload reset the filter, the URL keeps the user's filter (re-applied below)
                }
                grid.urlFilter = model || {};
                $rootScope.$applyAsync(function () {
                    writeParams(change, user ? 'push' : 'replace');
                });
            });
            api.addEventListener('sortChanged', function () {
                if (grid.restoring || applyingUrl) {
                    return;
                }
                $rootScope.$applyAsync(function () {
                    writeParams(makeChange(gridParam(grid, 's'), currentSort(grid)), 'replace');
                });
            });
            api.addEventListener('paginationChanged', function (event) {
                if (grid.pending) {
                    tryPending(grid);
                    return;
                }
                // grids without paging (the page pages on the server, or shows everything) have no page / page size
                if (grid.restoring || applyingUrl || !api.paginationGetCurrentPage || !grid.gridOptions.pagination) {
                    return;
                }
                var page = api.paginationGetCurrentPage() + 1;
                var size = api.paginationGetPageSize();
                var change = makeChange(gridParam(grid, 'p'), page > 1 ? String(page) : null);
                change[gridParam(grid, 'ps')] = !grid.autoPageSize && size && size !== grid.defaultPageSize ? String(size) : null;
                if (event && event.newPage) {
                    change[gridParam(grid, 'r')] = null;
                }
                $rootScope.$applyAsync(function () {
                    writeParams(change, 'replace');
                });
            });
            api.addEventListener('modelUpdated', function () {
                if (grid.pending) {
                    tryPending(grid);
                }
            });
            // new rows (e.g. after a date change) can drop a set-filter selection: put the URL's filter back
            api.addEventListener('rowDataChanged', function () {
                if (grid.pending || !grid.urlFilter || !Object.keys(grid.urlFilter).length) {
                    return;
                }
                if (!angular.equals(api.getFilterModel() || {}, grid.urlFilter)) {
                    setFilter(grid, grid.urlFilter);
                }
            });
            api.addEventListener('bodyScroll', function (event) {
                if (grid.restoring || applyingUrl || event.direction !== 'vertical') {
                    return;
                }
                if (scrollTimer) {
                    $timeout.cancel(scrollTimer);
                }
                scrollTimer = $timeout(function () {
                    var first = api.getFirstDisplayedRow ? api.getFirstDisplayedRow() : 0;
                    writeParams(makeChange(gridParam(grid, 'r'), first > 0 ? String(first) : null), 'replace');
                }, 400);
            });
        }

        // ---------- DataTables (jQuery) ----------

        // the table is found by its selector each time DataTables starts on it (angular-datatables re-creates it on new data)
        function attachDataTable(reg, key, selector) {
            var jq = $window.jQuery;
            var dt = { key: key, selector: selector, api: null, defaults: null, restoring: false, searchTimer: null, lastSearchWrite: 0,
                detach: angular.noop };
            reg.dataTables.push(dt);
            if (!jq || !jq.fn || !jq.fn.dataTable) {
                return;
            }
            var mine = function (settings) {
                return settings && settings.nTable && jq(settings.nTable).is(selector);
            };
            var onInit = function (event, settings) {
                if (!mine(settings)) {
                    return;
                }
                dt.api = new jq.fn.dataTable.Api(settings);
                if (!dt.defaults) {
                    dt.defaults = { len: dt.api.page.len(), order: encodeDtOrder(dt.api.order()) };
                }
                restoreDataTable(dt);
            };
            var onDraw = function (event, settings) {
                if (!mine(settings) || dt.restoring || applyingUrl || !dt.api) {
                    return;
                }
                $rootScope.$applyAsync(function () {
                    saveDataTable(dt);
                });
            };
            jq($window.document).on('init.dt', onInit).on('draw.dt', onDraw);
            dt.detach = function () {
                jq($window.document).off('init.dt', onInit).off('draw.dt', onDraw);
                if (dt.searchTimer) {
                    $timeout.cancel(dt.searchTimer);
                }
            };
        }

        function encodeDtOrder(order) {
            return (order || []).map(function (o) {
                return o[0] + ':' + o[1];
            }).join(',') || null;
        }

        function dtAlive(dt) {
            return !!dt.api && !!dt.api.settings()[0] && $window.document.body.contains(dt.api.table().node());
        }

        function saveDataTable(dt) {
            if (!dtAlive(dt)) {
                return;
            }
            var api = dt.api;
            var info = api.page.info();
            var order = encodeDtOrder(api.order());
            var change = makeChange(dt.key + '.p', info.page > 0 ? String(info.page + 1) : null);
            change[dt.key + '.ps'] = info.length !== dt.defaults.len ? String(info.length) : null;
            change[dt.key + '.s'] = order !== dt.defaults.order ? order : null;
            var search = api.search() || null;
            var searchInUrl = $location.search()[dt.key + '.q'] || null;
            writeParams(change, 'replace');
            if (search !== searchInUrl) {
                // typing in the table's search box: one history entry per pause
                if (dt.searchTimer) {
                    $timeout.cancel(dt.searchTimer);
                }
                dt.searchTimer = $timeout(function () {
                    dt.searchTimer = null;
                    var recent = Date.now() - dt.lastSearchWrite < 2500;
                    dt.lastSearchWrite = Date.now();
                    var c = makeChange(dt.key + '.q', api.search() || null);
                    c[dt.key + '.p'] = null;
                    writeParams(c, recent ? 'replace' : 'push');
                }, SEARCH_DEBOUNCE);
            }
        }

        function restoreDataTable(dt) {
            if (!dtAlive(dt)) {
                return;
            }
            var api = dt.api;
            var search = $location.search();
            var columns = api.columns().count();
            var len = codecs.int.decode(search[dt.key + '.ps'] || '');
            var order = String(search[dt.key + '.s'] || '').split(',').map(function (item) {
                var parts = item.split(':');
                var col = codecs.int.decode(parts[0] || '');
                return col !== undefined && col >= 0 && col < columns && (parts[1] === 'asc' || parts[1] === 'desc') ? [col, parts[1]] : null;
            }).filter(Boolean);
            var q = search[dt.key + '.q'];
            var page = codecs.int.decode(search[dt.key + '.p'] || '');
            // invalid values are dropped from the URL
            var junk = {};
            if (search[dt.key + '.p'] !== undefined && !(page >= 1)) {
                junk[dt.key + '.p'] = null;
            }
            if (search[dt.key + '.ps'] !== undefined && !(len > 0 && len <= 5000)) {
                junk[dt.key + '.ps'] = null;
            }
            if (search[dt.key + '.s'] !== undefined && order.length !== String(search[dt.key + '.s']).split(',').length) {
                junk[dt.key + '.s'] = null;
            }
            if (q !== undefined && !(typeof q === 'string' && q.length <= 500)) {
                junk[dt.key + '.q'] = null;
            }
            dt.restoring = true;
            try {
                api.page.len(len && len > 0 && len <= 5000 ? len : dt.defaults.len);
                api.order(order.length ? order : (dt.defaults.order ? dt.defaults.order.split(',').map(function (item) {
                    var parts = item.split(':');
                    return [parseInt(parts[0], 10), parts[1]];
                }) : []));
                api.search(typeof q === 'string' && q.length <= 500 ? q : '');
                api.draw();
                var pages = api.page.info().pages;
                var target = page && page > 1 ? Math.min(page, pages || 1) : 1; // page gone (fewer rows now): last page
                if (target > 1) {
                    api.page(target - 1).draw('page');
                }
                if (page && page > 1 && target !== page) {
                    junk[dt.key + '.p'] = target > 1 ? String(target) : null;
                }
                if (Object.keys(junk).length) {
                    writeParams(junk, 'replace');
                }
            } catch (e) {
                // a bad value in the URL: the table keeps its defaults
            } finally {
                dt.restoring = false;
            }
        }

        // ---------- ng-table ----------

        // tableParams (scope path) may be created again by the page; each new one gets the URL's page / page size
        function attachNgTable(reg, key, path) {
            var getter = $parse(path);
            var t = { key: key, current: null, defaultCount: null };
            reg.ngTables.push(t);
            reg.scope.$watch(function () {
                return getter(reg.scope);
            }, function (params) {
                if (params && angular.isFunction(params.page) && angular.isFunction(params.count)) {
                    t.current = params;
                    if (t.defaultCount === null) {
                        t.defaultCount = params.count();
                    }
                    restoreNgTable(t);
                }
            });
            reg.scope.$watch(function () {
                var params = t.current;
                return params ? params.page() + ':' + params.count() + ':' + (params.total() || 0) : null;
            }, function (value, previous) {
                if (!value || value === previous || applyingUrl) {
                    return;
                }
                var params = t.current;
                var pages = params.total() ? Math.ceil(params.total() / params.count()) : 0;
                if (pages && params.page() > pages) {
                    params.page(pages); // page gone (fewer rows now): last page
                    return;
                }
                var change = makeChange(key + '.p', params.page() > 1 ? String(params.page()) : null);
                change[key + '.ps'] = params.count() !== t.defaultCount ? String(params.count()) : null;
                writeParams(change, 'replace');
            });
        }

        function restoreNgTable(t) {
            var params = t.current;
            if (!params) {
                return;
            }
            var search = $location.search();
            var page = codecs.int.decode(search[t.key + '.p'] || '');
            var count = codecs.int.decode(search[t.key + '.ps'] || '');
            var junk = {};
            if (search[t.key + '.p'] !== undefined && !(page >= 1)) {
                junk[t.key + '.p'] = null;
            }
            if (search[t.key + '.ps'] !== undefined && !(count > 0 && count <= 500)) {
                junk[t.key + '.ps'] = null;
            }
            page = page >= 1 ? page : 1;
            count = count > 0 && count <= 500 ? count : t.defaultCount;
            if (params.count() !== count) {
                params.count(count); // ng-table goes back to page 1 here, so the page is set after
            }
            if (params.page() !== page) {
                params.page(page);
            }
            if (Object.keys(junk).length) {
                writeParams(junk, 'replace');
            }
        }

        // ---------- back to a list on another page ----------

        // last URL (with its query) seen for each page, e.g. a list with its filters, and the URL before the current
        // page; kept for this browser tab (sessionStorage) so a refresh does not lose them
        var STORE_KEY = 'scrapq.tableState.nav';
        var nav = readNav();

        function hashUrl(absoluteUrl) {
            var i = String(absoluteUrl || '').indexOf('#');
            return i < 0 ? null : String(absoluteUrl).slice(i + 1);
        }

        function readNav() {
            try {
                var saved = angular.fromJson($window.sessionStorage.getItem(STORE_KEY));
                if (saved && angular.isObject(saved.lastUrlByPath)) {
                    return saved;
                }
            } catch (e) {
                // storage blocked or bad value: start empty
            }
            return { lastUrlByPath: {}, previousUrl: null };
        }

        function saveNav() {
            try {
                $window.sessionStorage.setItem(STORE_KEY, angular.toJson(nav));
            } catch (e) {
                // storage blocked or full: only this page view remembers it
            }
        }

        $rootScope.$on('$locationChangeSuccess', function (event, newUrl, oldUrl) {
            var old = hashUrl(oldUrl);
            if (newUrl !== oldUrl && old && old.split('?')[0] !== $location.path()) {
                nav.previousUrl = old;
            }
            nav.lastUrlByPath[$location.path()] = $location.url();
            saveNav();
        });

        // a record page's Back to its list page: browser Back when the user came from there, else the list as last seen
        function backTo(path) {
            if (nav.previousUrl && nav.previousUrl.split('?')[0] === path) {
                $window.history.back();
                return;
            }
            $location.url(nav.lastUrlByPath[path] || path);
        }

        // ---------- back / forward on the same page ----------

        $rootScope.$on('$locationChangeSuccess', function (event, newUrl, oldUrl) {
            var url = $location.url();
            if (url === lastWrittenUrl) {
                return; // our own write
            }
            lastWrittenUrl = url;
            registrations.forEach(function (reg) {
                // only the page that stays; a page being left (another path) is closed by the router
                if (reg.stateName !== $state.current.name || reg.path !== $location.path()) {
                    return;
                }
                applyControls(reg, false);
                reg.grids.forEach(function (grid) {
                    restoreGrid(reg, grid);
                });
                reg.dataTables.forEach(function (dt) {
                    restoreDataTable(dt);
                });
                reg.ngTables.forEach(function (t) {
                    restoreNgTable(t);
                });
                if (reg.record) {
                    var id = $location.search()[reg.record.key] || null;
                    var open = reg.record.getter(reg.scope);
                    applyingUrl++;
                    try {
                        if (id && String(open || '') !== String(id) && /^[a-zA-Z0-9_-]{1,64}$/.test(id)) {
                            // Forward from the list: the entry before is the list, so in-app Back can use history
                            reg.cameFromList = isListUrl(reg, oldUrl);
                            reg.record.open(id);
                        } else if (!id && open) {
                            reg.cameFromList = false;
                            reg.record.close();
                        }
                    } finally {
                        applyingUrl--;
                    }
                }
            });
        });

        // same page as now, without this page's record open
        function isListUrl(reg, absoluteUrl) {
            var hash = String(absoluteUrl || '').split('#')[1];
            if (hash === undefined) {
                return false;
            }
            var path = hash.split('?')[0];
            var query = hash.split('?')[1] || '';
            return path === $location.path() && query.split('&').every(function (part) {
                return decodeURIComponent(part.split('=')[0]) !== reg.record.key;
            });
        }

        function backFromRecord(reg) {
            if (reg.cameFromList && reg.record && $location.search()[reg.record.key]) {
                reg.cameFromList = false;
                $window.history.back();
                return;
            }
            // opened directly (link / new tab): the list with the filters the link had, or the default list
            var search = currentSearch();
            if (reg.record) {
                delete search[reg.record.key];
            }
            applyingUrl++;
            try {
                if (reg.record) {
                    reg.record.close();
                }
            } finally {
                applyingUrl--;
            }
            $location.search(search);
            $location.replace();
        }

        function isModifiedClick(event) {
            var e = event && (event.event || event);
            return !!e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1);
        }

        function openInNewTab(reg, id) {
            if (!reg.record || !id) {
                return;
            }
            var search = currentSearch();
            search[reg.record.key] = String(id);
            var query = Object.keys(search).map(function (k) {
                return encodeURIComponent(k) + '=' + encodeURIComponent(search[k]);
            }).join('&');
            var base = $window.location.href.split('#')[0];
            $window.open(base + '#' + $location.path() + '?' + query, '_blank', 'noopener');
        }

        // ---------- sidebar: always the default view ----------

        $document.on('click', function (event) {
            var link = angular.element(event.target).closest('.side-menu a');
            if (!link.length || isModifiedClick(event)) {
                return;
            }
            var stateBefore = $state.current.name;
            var urlBefore = $location.url();
            // same page and same params: the router does nothing, so reset this page's values here
            $timeout(function () {
                if ($state.current.name === stateBefore && $location.url() === urlBefore) {
                    var search = currentSearch();
                    var mine = Object.keys(search).filter(function (k) {
                        return registrations.some(function (reg) {
                            return reg.stateName === stateBefore && k.indexOf(reg.ns + '.') === 0;
                        });
                    });
                    if (mine.length) {
                        mine.forEach(function (k) {
                            delete search[k];
                        });
                        $location.search(search); // our $locationChangeSuccess applies the defaults
                    }
                }
            }, 150);
        });

        return {
            page: page,
            backTo: backTo,
            isModifiedClick: isModifiedClick,
            // exposed for tests
            _codecs: codecs
        };
    });
